import React, { useState, useEffect } from 'react';
import { TextField, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography } from '@mui/material';
import { cropAndResizeImage } from '../utils/imageUtils';
import {
  fetchAgeGroups,
  addOrUpdateParticipant,
  uploadProfilePicture,
  AgeGroup,
} from '../api/contests';

interface ParticipantFormProps {
  participantId?: number;
  initialData?: any;
  onSubmitSuccess: () => void;
}

const ParticipantForm: React.FC<ParticipantFormProps> = ({ participantId, initialData, onSubmitSuccess }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [age, setAge] = useState<number | undefined>(initialData?.age || undefined);
  const [gender, setGender] = useState(initialData?.gender || '');
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<number | undefined>(initialData?.age_group_id || undefined);
  const [externalId, setExternalId] = useState(initialData?.external_id || '');
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [telegramId, setTelegramId] = useState<number | null>(null);

  useEffect(() => {
    fetchAgeGroups()
      .then((response) => setAgeGroups(response))
      .catch((error) => console.error('Error fetching age groups:', error));
  }, []);

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      cropAndResizeImage(file).then(({ resizedFile, resizedImageURI }) => {
        setProfilePicture(resizedFile);
        setProfilePicturePreview(resizedImageURI);
      });
    }
  };

  const handlePasteImage = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') === 0) {
        const file = items[i].getAsFile();
        if (file) {
          cropAndResizeImage(file).then(({ resizedFile, resizedImageURI }) => {
            setProfilePicture(resizedFile);
            setProfilePicturePreview(resizedImageURI);
          });
        }
      }
    }
  };

  const handleSubmit = async () => {
    if (!name || !gender || !selectedAgeGroup) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      let tempImageName = null;

      // Step 1: Upload the image first (if available)
      if (profilePicture) {
        tempImageName = await uploadProfilePicture(profilePicture);
      }

      // Step 2: Submit participant data (for new participant or update)
      const participantData = {
        name,
        age,
        gender,
        age_group_id: selectedAgeGroup,
        external_id: externalId || null,
        tempImageName, // Pass the image name directly to the server for association
        tg_id: telegramId || null,
      };
  
      const payload = {
        participantId: participantId || undefined,
        participantData,
      };
  
      await addOrUpdateParticipant(payload);

      alert('Participant saved successfully!');
      onSubmitSuccess(); // Call the passed callback to notify the parent of successful submission
    } catch (error) {
      console.error('Error submitting participant data:', error);
      alert('Error submitting participant data');
    }
  };

  return (
    <Box onPaste={handlePasteImage}>
      <Typography variant="h5" gutterBottom>
        {participantId ? 'Update Participant' : 'Add Participant'}
      </Typography>

      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        style={{ marginBottom: '16px' }}
      />

      <TextField
        label="Age"
        type="number"
        value={age || ''}
        onChange={(e) => setAge(Number(e.target.value))}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      <FormControl fullWidth style={{ marginBottom: '16px' }}>
        <InputLabel>Gender</InputLabel>
        <Select value={gender} onChange={(e) => setGender(e.target.value as string)} required>
          <MenuItem value="М">Male</MenuItem>
          <MenuItem value="Ж">Female</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth style={{ marginBottom: '16px' }}>
        <InputLabel>Age Group</InputLabel>
        <Select value={selectedAgeGroup || ''} onChange={(e) => setSelectedAgeGroup(Number(e.target.value))} required>
          {ageGroups.map((group) => (
            <MenuItem key={group.age_group_id} value={group.age_group_id}>
              {group.age_group_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="External ID"
        value={externalId}
        onChange={(e) => setExternalId(e.target.value)}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

    <TextField
        label="Tg ID"
        value={telegramId}
        onChange={(e) => setTelegramId(Number(e.target.value))}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      {/* Image upload */}
      <input type="file" onChange={handleProfilePictureChange} accept="image/*" style={{ marginTop: '16px' }} />
      {profilePicturePreview && (
        <Box mt={2}>
          <Typography variant="body1">Profile Picture Preview:</Typography>
          <img src={profilePicturePreview} alt="Profile Preview" style={{ width: '200px', height: '200px' }} />
        </Box>
      )}

      <Button variant="contained" color="primary" onClick={handleSubmit} style={{ marginTop: '16px' }}>
        {participantId ? 'Update Participant' : 'Add Participant'}
      </Button>
    </Box>
  );
};

export default ParticipantForm;
